import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Layout from '../components/Layout';
import {
  Plus,
  Search,
  CreditCard,
  FileText,
  BarChart3,
  Filter,
  Edit,
  Trash2,
  Receipt,
  Upload,
  Archive,
  Building2,
  Link as LinkIcon,
  Copy,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';

import ImportedNfseViewer from '../components/billing/ImportedNfseViewer';


import BillingHeader from '../components/billing/BillingHeader';
import BillingStatsCards from '../components/billing/BillingStatsCards';
import FinancialSummary from '../components/billing/FinancialSummary';
import CurrencyInput from '../components/common/CurrencyInput';
import PartnerManagementPanel from '../components/billing/PartnerManagementPanel';
import FinancialReportsPanel from '../components/billing/FinancialReportsPanel';
import ExpenseManagementPanel from '../components/billing/ExpenseManagementPanel';
import CashFlowProjectionPanel from '../components/billing/CashFlowProjectionPanel';
import FinancialInvoicesWorkspace from '../components/billing/workspaces/FinancialInvoicesWorkspace';
import ImportedNfseWorkspace from '../components/billing/workspaces/ImportedNfseWorkspace';
import FinancialPaymentsWorkspace from '../components/billing/workspaces/FinancialPaymentsWorkspace';
import FinancialExpensesWorkspace from '../components/billing/workspaces/FinancialExpensesWorkspace';
import FinancialReportsWorkspace from '../components/billing/workspaces/FinancialReportsWorkspace';
import FinancialPartnersWorkspace from '../components/billing/workspaces/FinancialPartnersWorkspace';

import api from '../services/api';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const BillingPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [financialPartners, setFinancialPartners] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [patients, setPatients] = useState([]);

  const [patientTreatments, setPatientTreatments] = useState([]);
  const [loadingPatientTreatments, setLoadingPatientTreatments] = useState(false);

  const [walletSummary, setWalletSummary] = useState(null);
  const [useWalletCredit, setUseWalletCredit] = useState(false);
  const [walletCreditValue, setWalletCreditValue] = useState('');

  const [patientWalletSummaries, setPatientWalletSummaries] = useState({});

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [stats, setStats] = useState({});
  const [financialReport, setFinancialReport] = useState({});
  const [cashFlowRefreshKey, setCashFlowRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);

  const today = new Date();

  const currentMonthValue = `${today.getFullYear()}-${String(
    today.getMonth() + 1
  ).padStart(2, '0')}`;

  const [periodMode, setPeriodMode] = useState('current_month');
  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [loadingPeriod, setLoadingPeriod] = useState(false);

  const initialStatusFilter = new URLSearchParams(location.search).get('status') || '';

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(
    initialStatusFilter === 'vencido' ? 'vencido' : ''
  );
  const [patientFilter, setPatientFilter] = useState('');
  const [nfseFilter, setNfseFilter] = useState('');
  const [externalPartnerFilter, setExternalPartnerFilter] = useState('');
  const [externalServiceFilter, setExternalServiceFilter] = useState('');
  const [externalCompetenceFilter, setExternalCompetenceFilter] = useState('');
  const [reportInvoiceFilter, setReportInvoiceFilter] = useState('');
  const [reportNavigationLabel, setReportNavigationLabel] = useState('');

  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [showExternalRevenueForm, setShowExternalRevenueForm] = useState(false);
  const [editingExternalRevenue, setEditingExternalRevenue] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [expandedInvoices, setExpandedInvoices] = useState({});
  const [selectedInvoiceInstallment, setSelectedInvoiceInstallment] = useState(null);

  const [paymentDestination, setPaymentDestination] = useState('invoice');

  const allowedTabs = ['invoices', 'payments', 'expenses', 'imported_nfse', 'partners', 'reports'];
  const initialTab = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTab] = useState(
    allowedTabs.includes(initialTab) ? initialTab : 'invoices'
  );
  const [nfseLoadingInvoiceId, setNfseLoadingInvoiceId] = useState(null);

  const [importedNfses, setImportedNfses] = useState([]);
  const [showImportedNfseViewer, setShowImportedNfseViewer] = useState(false);
  const [selectedImportedNfse, setSelectedImportedNfse] = useState(null);
  const [isUploadingNfse, setIsUploadingNfse] = useState(false);

  const [showLinkInvoiceModal, setShowLinkInvoiceModal] = useState(false);
  const [selectedNfseForLink, setSelectedNfseForLink] = useState(null);
  const [linkInvoiceId, setLinkInvoiceId] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  // overrides locais de status de NFS-e (para refletir imediatamente na tela)
  const [nfseOverrides, setNfseOverrides] = useState({});

  const [invoiceData, setInvoiceData] = useState({
    patient_id: '',
    patient_name: '',
    due_date: '',
    items: [{ description: '', quantity: 1, unit_price: 0, total_price: 0 }],
    discount_amount: 0,
    discount_percentage: 0,
    tax_amount: 0,
    tax_percentage: 0,
    payment_terms: '',
    notes: '',
    payment_type: 'single',
    installment_count: 2,
    first_installment_date: ''
  });

  const [paymentData, setPaymentData] = useState({
    invoice_id: '',
    invoice_installment_id: '',
    patient_id: '',
    patient_name: '',
    amount: 0,
    payment_method_id: '',
    payment_method_name: '',
    installment_number: null,
    total_installments: null,
    transaction_id: '',
    reference: '',
    notes: ''
  });

  const [externalRevenueData, setExternalRevenueData] = useState({
    partner_id: '',
    partner_name: 'SouSmile',
    custom_partner_name: '',
    service_category: 'aligners_dentistry',
    service_custom_description: '',
    competence: currentMonthValue,
    gross_amount: '',
    nfse_issue_date: '',
    nfse_number: '',
    received_amount: '',
    expected_receipt_date: '',
    receipt_date: '',
    status: 'faturado',
    notes: ''
  });

  // Modal de emissão de NFS-e
  const [nfseModalOpen, setNfseModalOpen] = useState(false);
  const [nfseModalInvoice, setNfseModalInvoice] = useState(null);
  const [nfseModalDescription, setNfseModalDescription] = useState('');
  const [nfseModalSaving, setNfseModalSaving] = useState(false);

  // Modal de detalhes da NFS-e (novo)
  const [nfseDetailsModalOpen, setNfseDetailsModalOpen] = useState(false);
  const [nfseDetailsInvoiceId, setNfseDetailsInvoiceId] = useState(null);
  const [nfseDetailsData, setNfseDetailsData] = useState(null);
  const [nfseDetailsLoading, setNfseDetailsLoading] = useState(false);

  const tabs = [
    {
      id: 'invoices',
      name: 'Contas a receber',
      description: 'Acompanhe valores cobrados e ainda pendentes',
      icon: FileText
    },
    {
      id: 'payments',
      name: 'Recebimentos',
      description: 'Registre e consulte valores recebidos',
      icon: CreditCard
    },
    {
      id: 'expenses',
      name: 'Despesas',
      description: 'Gerencie contas, parcelas e pagamentos',
      icon: Receipt
    },
    {
      id: 'imported_nfse',
      name: 'Documentos fiscais',
      description: 'Importe e vincule NFS-e às receitas',
      icon: Receipt
    },
    {
      id: 'partners',
      name: 'Outras receitas',
      description: 'Registre parceiros, comissões e receitas externas',
      icon: Building2
    },
    {
      id: 'reports',
      name: 'Relatórios',
      description: 'Analise resultados e indicadores',
      icon: BarChart3
    }
  ];

  const statusOptions = [
    { value: 'pendente', label: 'Pendente', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'parcialmente_pago', label: 'Parcialmente Pago', color: 'bg-blue-100 text-blue-800' },
    { value: 'pago', label: 'Pago', color: 'bg-green-100 text-green-800' },
    { value: 'vencido', label: 'Vencido', color: 'bg-red-100 text-red-800' },
    { value: 'cancelado', label: 'Cancelado', color: 'bg-gray-100 text-gray-800' },
    { value: 'faturado', label: 'Aguardando Recebimento', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'parcialmente_recebido', label: 'Recebido Parcialmente', color: 'bg-blue-100 text-blue-800' },
    { value: 'recebido', label: 'Recebido', color: 'bg-green-100 text-green-800' }
  ];

  const externalServiceLabels = {
    aligners_dentistry: 'Atendimento com Alinhadores',
    partner_network_consulting: 'Consultoria para Parceiros',
    training: 'Treinamento',
    lecture: 'Palestra',
    mentoring: 'Mentoria',
    course: 'Curso',
    clinical_consulting: 'Consultoria Clínica',
    product_sale: 'Venda de Produtos',
    commission: 'Comissão',
    bonus: 'Bonificação',
    other: 'Outro'
  };

  const getExternalServiceLabel = (invoice) =>
    invoice?.service_category === 'other'
      ? invoice?.service_custom_description || invoice?.service_description || 'Outro serviço'
      : externalServiceLabels[invoice?.service_category] || invoice?.service_description || 'Receita externa';

  const getEffectiveNfseStatus = (invoice) => {
    if (!invoice) return 'nao_emitida';
    const override = nfseOverrides[invoice.id];
    const base = invoice.nfse_status;
    return override || base || 'nao_emitida';
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab');
    const requestedStatus = params.get('status');

    if (allowedTabs.includes(requestedTab) && requestedTab !== activeTab) {
      setActiveTab(requestedTab);
    }

    if (requestedStatus === 'vencido') {
      setStatusFilter('vencido');
      setReportInvoiceFilter('overdue');
      setReportNavigationLabel('Cobranças vencidas');
    } else if (!requestedStatus && reportInvoiceFilter === 'overdue') {
      setStatusFilter('');
      setReportInvoiceFilter('');
      setReportNavigationLabel('');
    }
  }, [location.search]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);

    const params = new URLSearchParams(location.search);
    params.set('tab', tabId);
    navigate(`/financeiro?${params.toString()}`, { replace: true });
  };

  const clearReportNavigation = () => {
    setReportInvoiceFilter('');
    setReportNavigationLabel('');
    setStatusFilter('');
    setExternalPartnerFilter('');
    setExternalServiceFilter('');

    const params = new URLSearchParams(location.search);
    params.delete('status');
    navigate(`/financeiro?${params.toString()}`, { replace: true });
  };

  const handleReportNavigate = ({ target, filter = '', value = '', label = '' }) => {
    if (target === 'payments') {
      setReportInvoiceFilter('');
      setReportNavigationLabel(label || 'Recebimentos do período');
      handleTabChange('payments');
      return;
    }

    if (target === 'expenses') {
      setReportInvoiceFilter('');
      setReportNavigationLabel(label || 'Despesas do período');
      handleTabChange('expenses');
      return;
    }

    if (target !== 'invoices') return;

    setSearchTerm('');
    setPatientFilter('');
    setNfseFilter('');
    setStatusFilter('');
    setExternalPartnerFilter('');
    setExternalServiceFilter('');
    setExternalCompetenceFilter('');
    setReportInvoiceFilter(filter);
    setReportNavigationLabel(label);

    if (filter === 'overdue') setStatusFilter('vencido');
    if (filter === 'partner') setExternalPartnerFilter(value);
    if (filter === 'service') setExternalServiceFilter(value);

    handleTabChange('invoices');
  };

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const getPeriodParams = () => {
    if (periodMode === 'all') {
      return {
        period_mode: 'all'
      };
    }

    if (periodMode === 'month') {
      const [year, month] = selectedMonth.split('-').map(Number);
      const lastDay = new Date(year, month, 0).getDate();

      return {
        period_mode: 'month',
        start_date: `${selectedMonth}-01`,
        end_date: `${selectedMonth}-${String(lastDay).padStart(2, '0')}`
      };
    }

    if (periodMode === 'custom') {
      return {
        period_mode: 'custom',
        start_date: customStartDate,
        end_date: customEndDate
      };
    }

    return {
      period_mode: 'current_month'
    };
  };

  const fetchData = async () => {
    await Promise.all([
      fetchInvoices(),
      fetchPayments(),
      fetchPatients(),
      fetchPaymentMethods(),
      fetchStats(),
      fetchFinancialReport(),
      fetchImportedNfses(),
      fetchFinancialPartners(),
      fetchExpenses(),
      fetchExpenseCategories(),
      fetchSuppliers()
    ]);
    setLoading(false);
  };


  const fetchExpenses = async (periodParams = null) => {
    try {
      const response = await axios.get(`${API_URL}/billing/expenses`, {
        params: periodParams || getPeriodParams(),
        headers: authHeaders()
      });
      setExpenses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar despesas:', error);
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/billing/expense-categories`, {
        headers: authHeaders()
      });
      setExpenseCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar categorias de despesas:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/suppliers`, {
        headers: authHeaders()
      });
      setSuppliers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
    }
  };

  const reloadExpenses = async () => {
    await Promise.all([
      fetchExpenses(),
      fetchExpenseCategories(),
      fetchFinancialReport(),
      fetchStats()
    ]);
    setCashFlowRefreshKey((value) => value + 1);
  };

  const fetchFinancialPartners = async () => {
    try {
      const response = await axios.get(`${API_URL}/billing/partners`, {
        params: { include_inactive: true },
        headers: authHeaders()
      });
      setFinancialPartners(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar parceiros financeiros:', error);
    }
  };

  const fetchInvoices = async (periodParams = null) => {
    try {
      const response = await axios.get(`${API_URL}/billing/invoices`, {
        params: periodParams || getPeriodParams(),
        headers: authHeaders()
      });

      setInvoices(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar faturas:', error);
      toast?.error('Erro ao carregar faturas');
    }
  };

  const fetchPayments = async (periodParams = null) => {
    try {
      const response = await axios.get(`${API_URL}/billing/payments`, {
        params: periodParams || getPeriodParams(),
        headers: authHeaders()
      });

      setPayments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar pagamentos:', error);
    }
  };

  const correctPaymentDate = async (paymentId, paymentDate, reason) => {
    await axios.patch(`${API_URL}/billing/payments/${encodeURIComponent(paymentId)}/date`, {
      payment_date: paymentDate,
      reason
    }, { headers: authHeaders() });
    // Recarrega dados dependentes do período, pois o pagamento pode sair
    // da listagem atual ao ser movido para outro mês.
    await Promise.all([
      fetchPayments(), fetchInvoices(), fetchStats(), fetchFinancialReport()
    ]);
    setCashFlowRefreshKey((value) => value + 1);
    toast?.success('Data de recebimento corrigida. Confira os relatórios do período.');
  };

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API_URL}/patients`, {
        headers: authHeaders()
      });
      setPatients(response.data);
      fetchPatientsWalletSummaries(response.data);
    } catch (error) {
      console.error('Erro ao carregar pacientes:', error);
    }
  };

  const fetchPatientTreatments = async (patientId) => {
    if (!patientId) {
      setPatientTreatments([]);
      return;
    }

    try {
      setLoadingPatientTreatments(true);

      const response = await axios.get(`${API_URL}/treatments`, {
        params: { patient_id: patientId },
        headers: authHeaders()
      });

      setPatientTreatments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar tratamentos do paciente:', error);
      toast?.error('Erro ao carregar tratamentos do paciente.');
    } finally {
      setLoadingPatientTreatments(false);
    }
  };

  const fetchPatientWallet = async (patientId) => {
    if (!patientId) {
      setWalletSummary(null);
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/billing/patients/${patientId}/wallet`,
        {
          headers: authHeaders()
        }
      );

      setWalletSummary(response.data);
    } catch (err) {
      console.error(err);
      setWalletSummary(null);
    }
  };

  const fetchPatientsWalletSummaries = async (patientsList = []) => {
    if (!Array.isArray(patientsList) || patientsList.length === 0) {
      setPatientWalletSummaries({});
      return;
    }

    try {
      const results = await Promise.allSettled(
        patientsList.map((patient) =>
          axios.get(`${API_URL}/billing/patients/${patient.id}/wallet`, {
            headers: authHeaders()
          })
        )
      );

      const summaries = {};

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const patientId = patientsList[index]?.id;
          if (patientId) {
            summaries[patientId] = result.value.data;
          }
        }
      });

      setPatientWalletSummaries(summaries);
    } catch (error) {
      console.error('Erro ao carregar créditos dos pacientes:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API_URL}/billing/payment-methods`, {
        headers: authHeaders()
      });
      setPaymentMethods(response.data);
    } catch (error) {
      console.error('Erro ao carregar métodos de pagamento:', error);
    }
  };

  const fetchStats = async (periodParams = null) => {
    try {
      const response = await axios.get(`${API_URL}/billing/stats`, {
        params: periodParams || getPeriodParams(),
        headers: authHeaders()
      });

      setStats(response.data || {});
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const fetchFinancialReport = async (periodParams = null) => {
    try {
      const response = await axios.get(
        `${API_URL}/billing/reports/financial`,
        {
          params: periodParams || getPeriodParams(),
          headers: authHeaders()
        }
      );

      setFinancialReport(response.data || {});
    } catch (error) {
      console.error('Erro ao carregar relatório financeiro:', error);
    }
  };

  const reloadPeriodData = async () => {
    const params = getPeriodParams();

    if (
      periodMode === 'custom' &&
      (!customStartDate || !customEndDate)
    ) {
      toast?.error('Informe a data inicial e a data final.');
      return;
    }

    if (
      periodMode === 'custom' &&
      customStartDate > customEndDate
    ) {
      toast?.error('A data inicial não pode ser posterior à data final.');
      return;
    }

    try {
      setLoadingPeriod(true);

      await Promise.all([
        fetchInvoices(params),
        fetchPayments(params),
        fetchStats(params),
        fetchFinancialReport(params),
        fetchExpenses(params)
      ]);
    } finally {
      setCashFlowRefreshKey((value) => value + 1);
    setLoadingPeriod(false);
    }
  };

  useEffect(() => {
    if (loading) return;

    if (periodMode === 'custom') {
      return;
    }

    reloadPeriodData();
  }, [periodMode, selectedMonth]);

  const fetchImportedNfses = async () => {
    try {
      const response = await axios.get(`${API_URL}/imported-nfse`, {
        headers: authHeaders()
      });
      setImportedNfses(response.data);
    } catch (error) {
      console.error('Erro ao carregar NFS-e importadas:', error);
    }
  };

  const handleUploadNfse = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedExtensions = ['xml', 'pdf'];
    const maxFileSize = 20 * 1024 * 1024;
    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      const extension = String(file.name || '').split('.').pop()?.toLowerCase();
      if (!allowedExtensions.includes(extension)) {
        invalidFiles.push(`${file.name}: formato não permitido`);
      } else if (file.size > maxFileSize) {
        invalidFiles.push(`${file.name}: excede 20 MB`);
      } else {
        validFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      toast?.error(`Alguns arquivos não foram enviados: ${invalidFiles.join('; ')}`);
    }
    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    let importedCount = 0;
    const failedFiles = [];
    const reviewFiles = [];

    try {
      setIsUploadingNfse(true);
      for (const file of validFiles) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          const response = await axios.post(`${API_URL}/imported-nfse/upload`, formData, {
            headers: {
              ...authHeaders(),
              'Content-Type': 'multipart/form-data'
            }
          });
          importedCount += 1;

          const imported = response.data || {};
          const missingFields = [];
          if (!imported.numero_nfse) missingFields.push('número');
          if (!imported.data_emissao) missingFields.push('data de emissão');
          if (!imported.tomador_nome) missingFields.push('tomador');
          if (!imported.tomador_cpf_cnpj) missingFields.push('CPF/CNPJ');
          if (!Number(imported.valor_servico || 0)) missingFields.push('valor');
          if (missingFields.length > 0) {
            reviewFiles.push(`${file.name}: confira ${missingFields.join(', ')}`);
          }
        } catch (error) {
          console.error(`Erro ao importar ${file.name}:`, error);

          let message = 'Erro na importação';

          const detail = error?.response?.data?.detail;

          if (typeof detail === 'string') {
            message = detail;
          } else if (Array.isArray(detail)) {
            message = detail
              .map((d) => d.msg || d.message || JSON.stringify(d))
              .join(' | ');
          } else if (detail?.message) {
            message = detail.message;
          } else if (detail) {
            message = JSON.stringify(detail);
          }

          failedFiles.push(`${file.name}: ${message}`);
        }
      }

      if (importedCount > 0) {
        toast?.success(`${importedCount} NFS-e${importedCount > 1 ? 's' : ''} importada${importedCount > 1 ? 's' : ''} com sucesso!`);
        await fetchImportedNfses();
      }
      if (reviewFiles.length > 0) {
        toast?.warning?.(`Importação concluída, mas alguns dados precisam de conferência: ${reviewFiles.join('; ')}`)
          || toast?.error(`Importação concluída, mas confira os dados: ${reviewFiles.join('; ')}`);
      }
      if (failedFiles.length > 0) {
        const failureMessage =
          `Não foi possível importar ${failedFiles.length === 1 ? 'o arquivo' : 'os arquivos'}:\n\n` +
          failedFiles.join('\n\n');

        // Aviso nativo: não depende do ToastContext e sempre informa a rejeição.
        window.alert(failureMessage);

        // Mantém o toast apenas como retorno visual complementar.
        if (typeof toast?.error === 'function') {
          toast.error(
            `${failedFiles.length} arquivo${failedFiles.length > 1 ? 's' : ''} não ` +
            `${failedFiles.length > 1 ? 'puderam' : 'pôde'} ser importado${failedFiles.length > 1 ? 's' : ''}.`
          );
        }
      }
    } catch (unexpectedError) {
      console.error('Erro inesperado no fluxo de importação de NFS-e:', unexpectedError);

      const unexpectedMessage =
        unexpectedError?.response?.data?.detail ||
        unexpectedError?.message ||
        'Ocorreu um erro inesperado durante a importação da NFS-e.';

      window.alert(
        `Não foi possível concluir a importação.\n\n${String(unexpectedMessage)}`
      );
    } finally {
      setIsUploadingNfse(false);
      e.target.value = '';
    }
  };

  const handleDeleteImportedNfse = async (nfse) => {
    const item = typeof nfse === 'string'
      ? importedNfses.find((current) => current.id === nfse)
      : nfse;
    if (!item?.id) return;

    const number = item.numero_nfse || 'sem número';
    const value = Number(item.valor_servico || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
    const linkWarning = item.invoice_id
      ? '\n\nA nota está vinculada a uma fatura. A exclusão poderá remover essa associação.'
      : '';

    if (!window.confirm(`Excluir a NFS-e ${number}, de ${item.tomador_nome || 'tomador não identificado'}, no valor de ${value}?${linkWarning}`)) return;

    try {
      await axios.delete(`${API_URL}/imported-nfse/${item.id}`, {
        headers: authHeaders()
      });
      toast?.success('NFS-e excluída com sucesso!');
      await Promise.all([fetchImportedNfses(), fetchInvoices()]);
    } catch (error) {
      console.error('Erro ao excluir NFS-e:', error);
      toast?.error(error.response?.data?.detail || 'Erro ao excluir NFS-e');
    }
  };

  const handleDispenseNfse = async (invoiceId) => {
    if (!window.confirm('Marcar esta fatura como isenta de NFS-e (Dispensada ou Cortesia)?')) return;
    try {
      await axios.put(`${API_URL}/billing/invoices/${invoiceId}/dispense-nfse`, {}, {
        headers: authHeaders()
      });
      toast?.success('NFS-e dispensada com sucesso!');
      fetchInvoices();
    } catch (error) {
      console.error('Erro ao dispensar NFS-e:', error);
      toast?.error('Erro ao dispensar NFS-e');
    }
  };

  const handleLinkInvoice = async () => {
    if (!linkInvoiceId) {
      toast?.error('Selecione uma fatura para vincular.');
      return;
    }
    try {
      setIsLinking(true);
      await axios.put(`${API_URL}/imported-nfse/${selectedNfseForLink.id}/link-invoice`, {
        invoice_id: linkInvoiceId
      }, { headers: authHeaders() });
      
      toast?.success('NFS-e vinculada com sucesso!');
      setShowLinkInvoiceModal(false);
      setLinkInvoiceId('');
      fetchInvoices();
      fetchImportedNfses();
    } catch (error) {
      console.error('Erro ao vincular NFS-e:', error);
      toast?.error(error?.response?.data?.detail || 'Erro ao vincular NFS-e');
    } finally {
      setIsLinking(false);
    }
  };

  const openImportedNfseLinkModal = (nfse) => {
    setSelectedNfseForLink(nfse);
    setLinkInvoiceId(nfse?.invoice_id || '');
    setShowLinkInvoiceModal(true);
  };

  const openImportedNfseViewer = (nfse) => {
    setSelectedImportedNfse(nfse);
    setShowImportedNfseViewer(true);
  };

  const getNfseCandidateInvoices = (nfse) => {
    if (!nfse) return [];
    const nfseValue = Number(nfse.valor_servico || 0);
    const nfseDate = nfse.data_emissao ? new Date(nfse.data_emissao) : null;

    return invoices
      .filter((invoice) => {
        if (nfse.patient_id) return invoice.patient_id === nfse.patient_id;
        const nfseDocument = String(nfse.tomador_cpf_cnpj || '').replace(/\D/g, '');
        const invoiceDocument = String(invoice.patient_cpf || invoice.cpf || '').replace(/\D/g, '');
        return nfseDocument && invoiceDocument && nfseDocument === invoiceDocument;
      })
      .map((invoice) => {
        const invoiceValue = Number(invoice.total_amount || 0);
        const difference = Math.abs(invoiceValue - nfseValue);
        const invoiceDate = invoice.issue_date ? new Date(invoice.issue_date) : null;
        const daysDifference = nfseDate && invoiceDate
          ? Math.abs(Math.round((nfseDate.getTime() - invoiceDate.getTime()) / 86400000))
          : null;
        let score = 0;
        if (difference < 0.01) score += 100;
        else if (difference <= Math.max(10, nfseValue * 0.05)) score += 50;
        if (daysDifference !== null && daysDifference <= 7) score += 30;
        else if (daysDifference !== null && daysDifference <= 30) score += 15;
        if (!['cancelado', 'pago'].includes(String(invoice.status || '').toLowerCase())) score += 5;
        return { invoice, difference, daysDifference, score };
      })
      .sort((a, b) => b.score - a.score || a.difference - b.difference);
  };

  const resetExternalRevenueForm = () => {
    setExternalRevenueData({
      partner_id: financialPartners.find((partner) => partner.name === 'SouSmile')?.id || '',
      partner_name: 'SouSmile',
      custom_partner_name: '',
      service_category: 'aligners_dentistry',
      service_custom_description: '',
      competence: currentMonthValue,
      gross_amount: '',
      nfse_issue_date: '',
      nfse_number: '',
      received_amount: '',
      expected_receipt_date: '',
      receipt_date: '',
      status: 'faturado',
      notes: ''
    });
    setEditingExternalRevenue(null);
    setShowExternalRevenueForm(false);
  };

  const handleExternalRevenueSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...externalRevenueData,
      partner_id: externalRevenueData.partner_name === 'Outro' ? null : (externalRevenueData.partner_id || null),
      partner_name: externalRevenueData.partner_name === 'Outro'
        ? externalRevenueData.custom_partner_name.trim()
        : externalRevenueData.partner_name,
      gross_amount: Number(externalRevenueData.gross_amount || 0),
      received_amount: Number(externalRevenueData.received_amount || 0),
      nfse_issue_date: externalRevenueData.nfse_issue_date
        ? new Date(`${externalRevenueData.nfse_issue_date}T12:00:00`).toISOString()
        : null,
      expected_receipt_date: externalRevenueData.expected_receipt_date
        ? new Date(`${externalRevenueData.expected_receipt_date}T12:00:00`).toISOString()
        : null,
      receipt_date: externalRevenueData.receipt_date
        ? new Date(`${externalRevenueData.receipt_date}T12:00:00`).toISOString()
        : null
    };

    try {
      if (editingExternalRevenue) {
        await axios.put(
          `${API_URL}/billing/external-revenues/${editingExternalRevenue.id}`,
          payload,
          { headers: authHeaders() }
        );
        toast?.success('Receita externa atualizada com sucesso!');
      } else {
        await axios.post(`${API_URL}/billing/external-revenues`, payload, {
          headers: authHeaders()
        });
        toast?.success('Receita externa registrada com sucesso!');
      }

      resetExternalRevenueForm();
      await Promise.all([fetchInvoices(), fetchStats(), fetchFinancialReport()]);
    } catch (error) {
      console.error('Erro ao salvar receita externa:', error);
      toast?.error(error.response?.data?.detail || 'Erro ao salvar receita externa');
    }
  };

  const handleEditExternalRevenue = (invoice) => {
    setEditingExternalRevenue(invoice);
    setExternalRevenueData({
      partner_id: invoice.partner_id || financialPartners.find((partner) => partner.name === invoice.partner_name)?.id || '',
      partner_name: financialPartners.some((partner) => partner.name === invoice.partner_name) ? invoice.partner_name : 'Outro',
      custom_partner_name: financialPartners.some((partner) => partner.name === invoice.partner_name) ? '' : (invoice.partner_name || ''),
      service_category: invoice.service_category || 'aligners_dentistry',
      service_custom_description: invoice.service_custom_description || (invoice.service_category === 'other' ? invoice.service_description || '' : ''),
      competence: invoice.competence || currentMonthValue,
      gross_amount: invoice.gross_amount ?? invoice.total_amount ?? '',
      nfse_issue_date: invoice.nfse_issue_date
        ? new Date(invoice.nfse_issue_date).toISOString().split('T')[0]
        : '',
      nfse_number: invoice.nfse_numero || '',
      received_amount: invoice.received_amount || '',
      expected_receipt_date: invoice.expected_receipt_date
        ? new Date(invoice.expected_receipt_date).toISOString().split('T')[0]
        : '',
      receipt_date: invoice.receipt_date
        ? new Date(invoice.receipt_date).toISOString().split('T')[0]
        : '',
      status: invoice.status || 'faturado',
      notes: invoice.notes || ''
    });
    setShowExternalRevenueForm(true);
  };

  const handleDuplicateExternalRevenue = (invoice) => {
    const base = invoice.competence ? new Date(`${invoice.competence}-01T12:00:00`) : new Date();
    base.setMonth(base.getMonth() + 1);
    const nextCompetence = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}`;
    const knownPartners = financialPartners.map((partner) => partner.name);

    setEditingExternalRevenue(null);
    setExternalRevenueData({
      partner_id: invoice.partner_id || financialPartners.find((partner) => partner.name === invoice.partner_name)?.id || '',
      partner_name: knownPartners.includes(invoice.partner_name) ? invoice.partner_name : 'Outro',
      custom_partner_name: knownPartners.includes(invoice.partner_name) ? '' : (invoice.partner_name || ''),
      service_category: invoice.service_category || 'aligners_dentistry',
      service_custom_description: invoice.service_custom_description || (invoice.service_category === 'other' ? invoice.service_description || '' : ''),
      competence: nextCompetence,
      gross_amount: '', nfse_issue_date: '', nfse_number: '', received_amount: '', expected_receipt_date: '', receipt_date: '',
      status: 'faturado', notes: invoice.notes || ''
    });
    setShowExternalRevenueForm(true);
  };

  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();

    try {
      const processedItems = invoiceData.items.map((item) => ({
        ...item,
        total_price: item.quantity * item.unit_price
      }));

      const invoicePayload = {
        ...invoiceData,
        items: processedItems,
        due_date: invoiceData.due_date ? new Date(`${invoiceData.due_date}T12:00:00`).toISOString() : null,
        first_installment_date:
          invoiceData.payment_type === 'installment' && invoiceData.first_installment_date
            ? new Date(`${invoiceData.first_installment_date}T12:00:00`).toISOString()
            : null,
        installment_count:
          invoiceData.payment_type === 'installment'
            ? Number(invoiceData.installment_count || 2)
            : 1,
        wallet_credit_amount: getWalletCreditAmount(),
      };

      if (editingInvoice) {
        await axios.put(`${API_URL}/billing/invoices/${editingInvoice.id}`, invoicePayload, {
          headers: authHeaders()
        });
        toast?.success('Fatura atualizada com sucesso!');
      } else {
        await axios.post(`${API_URL}/billing/invoices`, invoicePayload, {
          headers: authHeaders()
        });

        const usedCredit = getWalletCreditAmount();

        toast?.success(
          usedCredit > 0
            ? `Fatura criada utilizando ${formatCurrency(usedCredit)} do crédito do paciente.`
            : 'Fatura criada com sucesso!'
        );
      }

      resetInvoiceForm();
      fetchInvoices();
      fetchStats();
      fetchFinancialReport();
    } catch (error) {
      console.error('Erro ao salvar fatura:', error);
      toast?.error(error.response?.data?.detail || 'Erro ao salvar fatura');
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload =
        paymentDestination === 'wallet'
          ? {
              ...paymentData,
              invoice_id: null,
            }
          : {
              ...paymentData,
              patient_id: undefined,
              patient_name: undefined,
            };

      if (paymentDestination === 'wallet' && !paymentData.patient_id) {
        toast?.error('Selecione o paciente para adicionar crédito.');
        return;
      }

      await axios.post(`${API_URL}/billing/payments`, payload, {
        headers: authHeaders()
      });

      toast?.success(
        paymentDestination === 'wallet'
          ? 'Crédito adicionado à carteira do paciente com sucesso!'
          : 'Pagamento registrado com sucesso!'
      );

      resetPaymentForm();
      fetchInvoices();
      fetchPayments();
      fetchStats();
      fetchFinancialReport();
      fetchPatientsWalletSummaries(patients);

      if (paymentDestination === 'wallet' && paymentData.patient_id) {
        fetchPatientWallet(paymentData.patient_id);
      }
    } catch (error) {
      console.error('Erro ao registrar pagamento:', error);
      toast?.error(error.response?.data?.detail || 'Erro ao registrar pagamento');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta fatura? Essa ação não poderá ser desfeita.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/billing/invoices/${invoiceId}`, {
        headers: authHeaders()
      });
      toast?.success('Fatura excluída com sucesso!');
      fetchInvoices();
      fetchStats();
      fetchFinancialReport();
    } catch (error) {
      console.error('Erro ao excluir fatura:', error);
      toast?.error(error.response?.data?.detail || 'Erro ao excluir fatura');
    }
  };

  const hasNfseEmitted = (invoice) => {
    const status = getEffectiveNfseStatus(invoice);
    return status === 'emitida' || status === 'rejeitada' || status === 'cancelada';
  };

  const canCancelNfse = (invoice) => {
    const status = getEffectiveNfseStatus(invoice);
    // por enquanto, só permite cancelar quando está emitida
    return status === 'emitida';
  };

  const handleEmitNfse = async (invoice) => {
    if (!window.confirm('Emitir NFS-e para esta fatura?')) {
      return;
    }

    try {
      setNfseLoadingInvoiceId(invoice.id);

      await axios.post(
        `${API_URL}/nfse/invoices/${invoice.id}/emitir`,
        null,
        {
          headers: authHeaders()
        }
      );

      toast?.success('NFS-e emitida (simulada) com sucesso!');

      // Atualiza visualmente
      setNfseOverrides((prev) => ({
        ...prev,
        [invoice.id]: 'emitida'
      }));

      // Garante sincronismo com o backend
      await fetchInvoices();
    } catch (error) {
      console.error('Erro ao emitir NFS-e:', error);
      const detail = error.response?.data?.detail || 'Erro ao emitir NFS-e';
      toast?.error(detail);
    } finally {
      setNfseLoadingInvoiceId(null);
    }
  };

  const handleCancelNfse = async (invoice) => {
    if (!window.confirm('Cancelar NFS-e desta fatura?')) {
      return;
    }

    try {
      setNfseLoadingInvoiceId(invoice.id);

      await axios.post(
        `${API_URL}/nfse/invoices/${invoice.id}/cancelar`,
        null,
        {
          headers: authHeaders()
        }
      );

      toast?.success('NFS-e cancelada (simulada) com sucesso!');

      // Atualiza visualmente
      setNfseOverrides((prev) => ({
        ...prev,
        [invoice.id]: 'cancelada'
      }));

      await fetchInvoices();
    } catch (error) {
      console.error('Erro ao cancelar NFS-e:', error);
      const detail = error.response?.data?.detail || 'Erro ao cancelar NFS-e';
      toast?.error(detail);
    } finally {
      setNfseLoadingInvoiceId(null);
    }
  };

  const addInvoiceItem = () => {
    setInvoiceData({
      ...invoiceData,
      items: [
        ...invoiceData.items,
        { description: '', quantity: 1, unit_price: 0, total_price: 0 }
      ]
    });
  };

  const addTreatmentToInvoice = (treatment) => {
    if (!treatment?.id) return;

    const alreadyAdded = invoiceData.items.some(
      (item) => item.treatment_id === treatment.id
    );

    if (alreadyAdded) {
      toast?.error('Este tratamento já foi adicionado à fatura.');
      return;
    }

    const value = Number(treatment.final_cost ?? treatment.cost ?? 0);

    setInvoiceData({
      ...invoiceData,
      items: [
        ...invoiceData.items.filter(
          (item) => item.description || Number(item.unit_price) > 0
        ),
        {
          description: treatment.procedure || 'Tratamento',
          quantity: 1,
          unit_price: value,
          total_price: value,
          treatment_id: treatment.id
        }
      ]
    });
  };

  const removeInvoiceItem = (index) => {
    const newItems = invoiceData.items.filter((_, i) => i !== index);
    setInvoiceData({ ...invoiceData, items: newItems });
  };

  const updateInvoiceItem = (index, field, value) => {
    const newItems = [...invoiceData.items];
    newItems[index] = { ...newItems[index], [field]: value };

    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total_price = newItems[index].quantity * newItems[index].unit_price;
    }

    setInvoiceData({ ...invoiceData, items: newItems });
  };

  const resetInvoiceForm = () => {
    setInvoiceData({
      patient_id: '',
      patient_name: '',
      due_date: '',
      items: [{ description: '', quantity: 1, unit_price: 0, total_price: 0 }],
      discount_amount: 0,
      discount_percentage: 0,
      tax_amount: 0,
      tax_percentage: 0,
      payment_terms: '',
      notes: '',
      payment_type: 'single',
      installment_count: 2,
      first_installment_date: ''
    });
    setEditingInvoice(null);
    setShowInvoiceForm(false);
    setWalletSummary(null);
    setUseWalletCredit(false);
    setWalletCreditValue('');    
  };

  const resetPaymentForm = () => {
    setPaymentData({
      invoice_id: '',
      invoice_installment_id: '',
      patient_id: '',
      patient_name: '',
      amount: 0,
      payment_method_id: '',
      payment_method_name: '',
      installment_number: null,
      total_installments: null,
      transaction_id: '',
      reference: '',
      notes: ''
    });

    setPaymentDestination('invoice');
    setShowPaymentForm(false);
    setSelectedInvoice(null);
    setSelectedInvoiceInstallment(null);
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setInvoiceData({
      patient_id: invoice.patient_id,
      patient_name: invoice.patient_name,
      due_date: invoice.due_date
        ? new Date(invoice.due_date).toISOString().split('T')[0]
        : '',
      items:
        invoice.items && invoice.items.length > 0
          ? invoice.items
          : [{ description: '', quantity: 1, unit_price: 0, total_price: 0 }],
      discount_amount: invoice.discount_amount || 0,
      discount_percentage: invoice.discount_percentage || 0,
      tax_amount: invoice.tax_amount || 0,
      tax_percentage: invoice.tax_percentage || 0,
      payment_terms: invoice.payment_terms || '',
      notes: invoice.notes || '',
      payment_type: invoice.payment_type || 'single',
      installment_count: invoice.installment_count || 2,
      first_installment_date: invoice.first_installment_date
        ? new Date(invoice.first_installment_date).toISOString().split('T')[0]
        : ''
    });
    setShowInvoiceForm(true);
    fetchPatientTreatments(invoice.patient_id);
    fetchPatientWallet(invoice.patient_id);
  };

  const handleAddPayment = (invoice, installment = null) => {
    const invoiceReceived = Number(invoice.received_amount || 0);
    const invoiceBalance = Math.max(Number(invoice.total_amount || 0) - invoiceReceived, 0);
    const installmentBalance = installment
      ? Math.max(Number(installment.amount || 0) - Number(installment.received_amount || 0), 0)
      : invoiceBalance;

    setSelectedInvoice(invoice);
    setSelectedInvoiceInstallment(installment);
    setPaymentDestination('invoice');
    setPaymentData({
      ...paymentData,
      invoice_id: invoice.id,
      invoice_installment_id: installment?.id || '',
      patient_id: invoice.patient_id,
      patient_name: invoice.patient_name,
      amount: installmentBalance
    });
    setShowPaymentForm(true);
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      searchTerm === '' ||
      invoice.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.partner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.service_description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === '' || invoice.status === statusFilter;
    const matchesPatient =
      patientFilter === '' ||
      (patientFilter === 'external' && invoice.invoice_type === 'non_patient') ||
      invoice.patient_id === patientFilter;

    const nfseStatus = getEffectiveNfseStatus(invoice);
    const hasNfse = nfseStatus !== 'nao_emitida';

    const matchesNfse =
      nfseFilter === '' ||
      (nfseFilter === 'com_nfse' && hasNfse) ||
      (nfseFilter === 'sem_nfse' && !hasNfse) ||
      (nfseFilter === 'emitida' && nfseStatus === 'emitida') ||
      (nfseFilter === 'rejeitada' && nfseStatus === 'rejeitada') ||
      (nfseFilter === 'cancelada' && nfseStatus === 'cancelada');

    const matchesExternalPartner = externalPartnerFilter === '' || invoice.invoice_type !== 'non_patient' || invoice.partner_name === externalPartnerFilter;
    const matchesExternalService = externalServiceFilter === '' || invoice.invoice_type !== 'non_patient' || invoice.service_category === externalServiceFilter;
    const matchesExternalCompetence = externalCompetenceFilter === '' || invoice.invoice_type !== 'non_patient' || invoice.competence === externalCompetenceFilter;

    const receivableStatuses = [
      'pendente',
      'parcialmente_pago',
      'vencido',
      'faturado',
      'parcialmente_recebido'
    ];
    const matchesReportNavigation =
      reportInvoiceFilter !== 'receivables' || receivableStatuses.includes(invoice.status);

    return matchesSearch && matchesStatus && matchesPatient && matchesNfse && matchesExternalPartner && matchesExternalService && matchesExternalCompetence && matchesReportNavigation;
  });

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find((s) => s.value === status);
    return statusOption ? statusOption.color : 'bg-gray-100 text-gray-800';
  };

  const getNfseStatusLabel = (invoice) => {
    const status = getEffectiveNfseStatus(invoice);

    if (status === 'nao_emitida' || !status) return 'NFS-e não emitida';
    if (status === 'emitida') return 'NFS-e emitida';
    if (status === 'rejeitada') return 'NFS-e rejeitada';
    if (status === 'cancelada') return 'NFS-e cancelada';

    return `NFS-e: ${status}`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const calculateInvoiceSubtotal = () => {
    return invoiceData.items.reduce(
      (sum, item) => sum + item.quantity * item.unit_price,
      0
    );
  };

  const calculateInvoiceTotal = () => {
    const subtotal = calculateInvoiceSubtotal();
    const discount =
      invoiceData.discount_percentage > 0
        ? subtotal * (invoiceData.discount_percentage / 100)
        : Number(invoiceData.discount_amount) || 0;
    const afterDiscount = subtotal - discount;
    const tax =
      invoiceData.tax_percentage > 0
        ? afterDiscount * (invoiceData.tax_percentage / 100)
        : Number(invoiceData.tax_amount) || 0;
    return afterDiscount + tax;
  };

  const getWalletCreditAmount = () => {
    if (!useWalletCredit) return 0;

    const typedValue = Number(walletCreditValue || 0);
    const availableBalance = Number(walletSummary?.available_balance || 0);
    const invoiceTotal = Number(calculateInvoiceTotal() || 0);

    return Math.min(
      Math.max(typedValue, 0),
      availableBalance,
      invoiceTotal
    );
  };

  const getInvoiceRemainingAmount = () => {
    return Math.max(calculateInvoiceTotal() - getWalletCreditAmount(), 0);
  };

  const buildDefaultNfseDescription = (invoice) => {
    if (!invoice) return '';

    const items = Array.isArray(invoice.items) ? invoice.items : [];

    const lines = items
      .map((item, index) => {
        const desc =
          item.description ||
          item.service_description ||
          item.name ||
          item.title ||
          '';

        if (!desc) return '';

        const parts = [`${index + 1}. ${desc}`];

        if (item.quantity != null) {
          parts.push(`Qtd: ${item.quantity}`);
        }

        if (item.total != null) {
          try {
            const totalFmt = Number(item.total).toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            });
            parts.push(`Total: ${totalFmt}`);
          } catch {
            // ignora erro de formatação
          }
        }

        return parts.join(' - ');
      })
      .filter((line) => line.trim().length > 0);

    if (lines.length === 0) {
      return 'Serviços odontológicos prestados.';
    }

    return lines.join('\n');
  };

  // Modal de emissão
  const handleOpenNfseModal = (invoice) => {
    if (!invoice) return;

    const baseDescription = buildDefaultNfseDescription(invoice);
    setNfseModalInvoice(invoice);
    setNfseModalDescription(baseDescription);
    setNfseModalOpen(true);
  };

  const handleCloseNfseModal = () => {
    setNfseModalOpen(false);
    setNfseModalInvoice(null);
    setNfseModalDescription('');
  };

  const handleConfirmNfseEmission = async () => {
    if (!nfseModalInvoice) return;

    try {
      setNfseModalSaving(true);

      const payload = {
        descricao_servico: nfseModalDescription
      };

      const response = await api.post(
        `/nfse/invoices/${nfseModalInvoice.id}/emitir`,
        payload
      );

      setInvoices((prev) =>
        prev.map((inv) =>
          inv.id === nfseModalInvoice.id ? { ...inv, ...response.data } : inv
        )
      );

      handleCloseNfseModal();
    } catch (error) {
      console.error('Erro ao emitir NFS-e:', error);
      alert(
        'Erro ao emitir NFS-e. Verifique os dados fiscais e tente novamente. Se o erro persistir, contate o suporte.'
      );
    } finally {
      setNfseModalSaving(false);
    }
  };

  // Abrir detalhes da NFS-e (busca no backend)
  const handleOpenNfseDetails = async (invoice) => {
    if (!invoice || !invoice.id) return;

    setNfseDetailsModalOpen(true);
    setNfseDetailsInvoiceId(invoice.id);
    setNfseDetailsLoading(true);
    setNfseDetailsData(null);

    try {
      const response = await axios.get(
        `${API_URL}/nfse/invoices/${invoice.id}/status`,
        { headers: authHeaders() }
      );
      setNfseDetailsData(response.data);
    } catch (error) {
      console.error('Erro ao carregar detalhes da NFS-e:', error);
      toast?.error(
        'Não foi possível carregar os detalhes da NFS-e. Tente novamente. Se o erro persistir, contate o suporte.'
      );
      setNfseDetailsModalOpen(false);
    } finally {
      setNfseDetailsLoading(false);
    }
  };

  const handleCloseNfseDetails = () => {
    setNfseDetailsModalOpen(false);
    setNfseDetailsInvoiceId(null);
    setNfseDetailsData(null);
    setNfseDetailsLoading(false);
  };

  const handleDownloadNfseXml = () => {
    if (!nfseDetailsData || !nfseDetailsData.nfse_xml) {
      alert('Nenhum XML de NFS-e disponível para esta fatura.');
      return;
    }

    try {
      const xmlContent = nfseDetailsData.nfse_xml;
      const blob = new Blob([xmlContent], {
        type: 'application/xml;charset=utf-8'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      const numero = nfseDetailsData.nfse_numero || nfseDetailsInvoiceId || 'nfse';
      link.href = url;
      link.download = `nfse_${numero}.xml`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar XML da NFS-e:', error);
      alert('Erro ao gerar o download do XML da NFS-e.');
    }
  };

  const handleDownloadNfsePdf = async () => {
    if (!nfseDetailsInvoiceId) {
      alert('Nenhuma fatura selecionada para gerar o PDF da NFS-e.');
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/nfse/invoices/${nfseDetailsInvoiceId}/pdf`,
        {
          responseType: 'blob',
          headers: authHeaders()
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      const numero = nfseDetailsData?.nfse_numero || nfseDetailsInvoiceId || 'nfse';
      link.href = url;
      link.download = `nfse_${numero}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar PDF da NFS-e:', error);
      alert('Erro ao baixar PDF da NFS-e.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <BillingHeader
          onNewInvoice={() => {
            resetInvoiceForm();
            setShowInvoiceForm(true);
          }}
          onNewExternalRevenue={() => {
            resetExternalRevenueForm();
            setShowExternalRevenueForm(true);
          }}
          onNewPayment={() => setShowPaymentForm(true)}
        />

        <section className="space-y-6">
          <div className="rounded-2xl border border-purple-200 bg-purple-50/50 p-5 sm:p-6">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
              <div className="max-w-2xl">
                <div className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-purple-700 ring-1 ring-purple-200">
                  Painel executivo
                </div>
                <h2 className="mt-3 text-2xl font-bold text-gray-900">
                  Visão financeira da clínica
                </h2>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  Acompanhe receitas, despesas, cobranças, resultado operacional e projeções do período selecionado.
                </p>
              </div>

              <div className="w-full rounded-xl border border-purple-100 bg-white p-4 xl:max-w-3xl">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4 xl:items-end">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Período dos indicadores
                    </label>
                    <select
                      value={periodMode}
                      onChange={(e) => setPeriodMode(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 p-2.5 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
                    >
                      <option value="current_month">Mês atual</option>
                      <option value="month">Mês específico</option>
                      <option value="custom">Período personalizado</option>
                      <option value="all">Todo o período</option>
                    </select>
                  </div>

                  {periodMode === 'month' && (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Mês
                      </label>
                      <input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 p-2.5 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
                      />
                    </div>
                  )}

                  {periodMode === 'custom' && (
                    <>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Data inicial
                        </label>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 p-2.5 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Data final
                        </label>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 p-2.5 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={reloadPeriodData}
                        disabled={loadingPeriod}
                        className="inline-flex min-h-[42px] items-center justify-center rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {loadingPeriod ? 'Atualizando...' : 'Aplicar período'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <BillingStatsCards
            stats={stats}
            financialReport={financialReport}
            formatCurrency={formatCurrency}
          />

          <FinancialSummary
            financialReport={financialReport}
            formatCurrency={formatCurrency}
          />

          <CashFlowProjectionPanel
            formatCurrency={formatCurrency}
            refreshKey={cashFlowRefreshKey}
            toast={toast}
          />
        </section>

        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t-2 border-gray-200"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="rounded-full border border-gray-200 bg-gray-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Operação financeira
            </span>
          </div>
        </div>

        <section className="overflow-hidden rounded-2xl border-2 border-purple-200 bg-white shadow-sm">
          <div className="border-b border-purple-100 bg-gradient-to-r from-purple-50 to-white px-5 py-5 sm:px-6">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-purple-600 p-2.5 text-white">
                    <CreditCard className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Central Operacional Financeira
                    </h2>
                    <p className="mt-1 text-sm text-gray-600">
                      Realize cobranças, recebimentos, despesas, operações fiscais e consultas gerenciais.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-purple-100 bg-white px-3 py-2 text-sm text-gray-600">
                Área ativa:
                <span className="ml-1 font-semibold text-purple-700">
                  {tabs.find((tab) => tab.id === activeTab)?.name}
                </span>
              </div>
            </div>
          </div>

          <nav
            className="border-b border-gray-200 bg-gray-50 px-3 py-3 sm:px-4"
            aria-label="Navegação operacional financeira"
          >
            <div className="flex gap-2 overflow-x-auto pb-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabChange(tab.id)}
                    aria-current={isActive ? 'page' : undefined}
                    title={tab.description}
                    className={`group relative inline-flex min-w-max flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-all xl:min-w-0 ${
                      isActive
                        ? 'border-purple-600 bg-purple-600 text-white shadow-sm'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 flex-none ${
                        isActive
                          ? 'text-white'
                          : 'text-purple-600 group-hover:text-purple-700'
                      }`}
                    />
                    <span className="whitespace-nowrap">{tab.name}</span>

                    {isActive && (
                      <span
                        className="absolute -bottom-[13px] left-1/2 h-1 w-10 -translate-x-1/2 rounded-full bg-purple-600"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </nav>

          <div className="p-5 sm:p-6">
            {activeTab === 'invoices' && (
              <FinancialInvoicesWorkspace
                reportNavigationLabel={reportNavigationLabel}
                reportInvoiceFilter={reportInvoiceFilter}
                statusFilter={statusFilter}
                externalPartnerFilter={externalPartnerFilter}
                externalServiceFilter={externalServiceFilter}
                clearReportNavigation={clearReportNavigation}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                statusOptions={statusOptions}
                patientFilter={patientFilter}
                setPatientFilter={setPatientFilter}
                patients={patients}
                filteredInvoices={filteredInvoices}
                invoices={invoices}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                patientWalletSummaries={patientWalletSummaries}
                getExternalServiceLabel={getExternalServiceLabel}
                getStatusColor={getStatusColor}
                handleEditExternalRevenue={handleEditExternalRevenue}
                handleEditInvoice={handleEditInvoice}
                handleDuplicateExternalRevenue={handleDuplicateExternalRevenue}
                handleAddPayment={handleAddPayment}
                handleDispenseNfse={handleDispenseNfse}
                handleDeleteInvoice={handleDeleteInvoice}
                expandedInvoices={expandedInvoices}
                setExpandedInvoices={setExpandedInvoices}
                setStatusFilter={setStatusFilter}
                onNewInvoice={() => {
                  resetInvoiceForm();
                  setShowInvoiceForm(true);
                }}
                onNewExternalRevenue={() => {
                  resetExternalRevenueForm();
                  setShowExternalRevenueForm(true);
                }}
              />
            )}

            {activeTab === 'imported_nfse' && (
              <ImportedNfseWorkspace
                isUploadingNfse={isUploadingNfse}
                handleUploadNfse={handleUploadNfse}
                importedNfses={importedNfses}
                invoices={invoices}
                onOpenLink={openImportedNfseLinkModal}
                onOpenViewer={openImportedNfseViewer}
                handleDeleteImportedNfse={handleDeleteImportedNfse}
              />
            )}

            {activeTab === 'payments' && (
              <FinancialPaymentsWorkspace
                payments={payments}
                invoices={invoices}
                paymentMethods={paymentMethods}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                canCorrectPaymentDate={Array.isArray(user?.permissions) && user.permissions.includes('billing.correct_payment_date')}
                onCorrectPaymentDate={correctPaymentDate}
              />
            )}


            {activeTab === 'expenses' && (
              <FinancialExpensesWorkspace
                expenses={expenses}
                categories={expenseCategories}
                suppliers={suppliers}
                paymentMethods={paymentMethods}
                periodParams={getPeriodParams()}
                formatCurrency={formatCurrency}
                onReload={reloadExpenses}
                toast={toast}
              />
            )}

            {activeTab === 'reports' && (
              <FinancialReportsWorkspace
                report={financialReport}
                formatCurrency={formatCurrency}
                onNavigate={handleReportNavigate}
              />
            )}
            {activeTab === 'partners' && (
              <FinancialPartnersWorkspace
                periodParams={getPeriodParams()}
                onPartnersChanged={fetchFinancialPartners}
                toast={toast}
              />
            )}
          </div>

        </section>

        {/* External Revenue Form Modal */}
        {showExternalRevenueForm && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) resetExternalRevenueForm();
            }}
          >
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center gap-3">
                <Building2 className="w-5 h-5 text-indigo-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingExternalRevenue ? 'Editar Receita Externa' : 'Nova Receita Externa'}
                </h3>
              </div>

              <form onSubmit={handleExternalRevenueSubmit} className="p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parceiro *</label>
                    <select
                      value={externalRevenueData.partner_name}
                      onChange={(e) => {
                        const selectedName = e.target.value;
                        const selectedPartner = financialPartners.find((partner) => partner.name === selectedName);
                        setExternalRevenueData({
                          ...externalRevenueData,
                          partner_id: selectedPartner?.id || '',
                          partner_name: selectedName,
                          custom_partner_name: selectedName === 'Outro' ? externalRevenueData.custom_partner_name : ''
                        });
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      required
                    >
                      {financialPartners.filter((partner) => partner.is_active).map((partner) => (
                        <option key={partner.id} value={partner.name}>{partner.name}</option>
                      ))}
                      <option value="Outro">Outro</option>
                    </select>
                    {externalRevenueData.partner_name === 'Outro' && <input type="text" value={externalRevenueData.custom_partner_name} onChange={(e) => setExternalRevenueData({ ...externalRevenueData, custom_partner_name: e.target.value })} className="w-full mt-2 border border-gray-300 rounded-lg px-3 py-2" placeholder="Nome do parceiro" required />}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Serviço Prestado *</label>
                    <select value={externalRevenueData.service_category} onChange={(e) => setExternalRevenueData({ ...externalRevenueData, service_category: e.target.value, service_custom_description: e.target.value === 'other' ? externalRevenueData.service_custom_description : '' })} className="w-full border border-gray-300 rounded-lg px-3 py-2" required>
                      <option value="aligners_dentistry">Atendimento com Alinhadores</option><option value="partner_network_consulting">Consultoria para Parceiros</option><option value="training">Treinamento</option><option value="lecture">Palestra</option><option value="mentoring">Mentoria</option><option value="course">Curso</option><option value="clinical_consulting">Consultoria Clínica</option><option value="product_sale">Venda de Produtos</option><option value="commission">Comissão</option><option value="bonus">Bonificação</option><option value="other">Outro</option>
                    </select>
                    {externalRevenueData.service_category === 'other' && <input type="text" value={externalRevenueData.service_custom_description} onChange={(e) => setExternalRevenueData({ ...externalRevenueData, service_custom_description: e.target.value })} className="w-full mt-2 border border-gray-300 rounded-lg px-3 py-2" placeholder="Descrição do serviço prestado" required />}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Competência *</label>
                    <input type="month" value={externalRevenueData.competence} onChange={(e) => setExternalRevenueData({ ...externalRevenueData, competence: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor Faturado *</label>
                    <CurrencyInput
                      value={externalRevenueData.gross_amount}
                      onValueChange={(value) => setExternalRevenueData({ ...externalRevenueData, gross_amount: value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data da Emissão</label>
                    <input type="date" value={externalRevenueData.nfse_issue_date} onChange={(e) => setExternalRevenueData({ ...externalRevenueData, nfse_issue_date: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Número da Nota Fiscal</label>
                    <input type="text" value={externalRevenueData.nfse_number} onChange={(e) => setExternalRevenueData({ ...externalRevenueData, nfse_number: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data do Recebimento</label>
                    <input type="date" value={externalRevenueData.receipt_date} onChange={(e) => setExternalRevenueData({ ...externalRevenueData, receipt_date: e.target.value, received_amount: e.target.value && !externalRevenueData.received_amount ? externalRevenueData.gross_amount : externalRevenueData.received_amount, status: e.target.value && !externalRevenueData.received_amount ? 'recebido' : externalRevenueData.status })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor Recebido</label>
                    <CurrencyInput
                      value={externalRevenueData.received_amount}
                      onValueChange={(value) => {
                        const numeric = Number(value || 0);
                        const gross = Number(externalRevenueData.gross_amount || 0);
                        setExternalRevenueData({
                          ...externalRevenueData,
                          received_amount: value,
                          status: numeric >= gross && gross > 0
                            ? 'recebido'
                            : numeric > 0
                              ? 'parcialmente_recebido'
                              : 'faturado'
                        });
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Previsão de Recebimento</label>
                    <input type="date" value={externalRevenueData.expected_receipt_date} onChange={(e) => setExternalRevenueData({ ...externalRevenueData, expected_receipt_date: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                    <select value={externalRevenueData.status} onChange={(e) => setExternalRevenueData({ ...externalRevenueData, status: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" required>
                      <option value="faturado">Aguardando Recebimento</option>
                      <option value="parcialmente_recebido">Recebido Parcialmente</option>
                      <option value="recebido">Recebido</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea rows="3" value={externalRevenueData.notes} onChange={(e) => setExternalRevenueData({ ...externalRevenueData, notes: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={resetExternalRevenueForm} className="px-4 py-2 border border-gray-300 rounded-lg">Cancelar</button>
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                    {editingExternalRevenue ? 'Salvar Alterações' : 'Registrar Receita'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Invoice Form Modal */}
        {showInvoiceForm && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                resetInvoiceForm();
              }
            }}
          >
            <div
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingInvoice ? 'Editar Fatura' : 'Nova Fatura'}
                </h3>
              </div>

              <form onSubmit={handleInvoiceSubmit} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Paciente *
                    </label>
                    <select
                      required
                      value={invoiceData.patient_id}
                      onChange={(e) => {
                        const patient = patients.find(p => p.id === e.target.value);

                        setInvoiceData({
                          ...invoiceData,
                          patient_id: e.target.value,
                          patient_name: patient?.full_name || ''
                        });

                        fetchPatientTreatments(e.target.value);
                        fetchPatientWallet(e.target.value);
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Selecione um paciente</option>
                      {patients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.full_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Vencimento
                    </label>
                    <input
                      type="date"
                      value={invoiceData.due_date}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          due_date: e.target.value
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                {invoiceData.patient_id && (
                  <div className="border border-purple-200 bg-purple-50 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <div>
                        <p className="text-sm font-semibold text-purple-900">
                          Tratamentos salvos do paciente
                        </p>
                        <p className="text-xs text-purple-700">
                          Adicione tratamentos já cadastrados diretamente à fatura.
                        </p>
                      </div>
                    </div>

                    {walletSummary && (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-4">

                    <div className="flex justify-between items-center">

                    <div>

                    <p className="text-sm font-semibold text-green-900">
                    Crédito disponível do paciente
                    </p>

                    <p className="text-2xl font-bold text-green-700">
                    {formatCurrency(walletSummary.available_balance)}
                    </p>

                    </div>

                    </div>

                    {walletSummary.available_balance > 0 && (

                    <div className="mt-4 space-y-3">

                    <label className="flex items-center gap-2">

                    <input
                    type="checkbox"
                    checked={useWalletCredit}
                    onChange={(e)=>setUseWalletCredit(e.target.checked)}
                    />

                    <span className="text-sm">
                    Utilizar crédito nesta fatura
                    </span>

                    </label>

                    {useWalletCredit && (

                    <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={walletSummary.available_balance}
                    value={walletCreditValue}
                    onChange={(e) => {
                      const raw = Number(e.target.value || 0);
                      const maxAllowed = Math.min(
                        Number(walletSummary?.available_balance || 0),
                        Number(calculateInvoiceTotal() || 0)
                      );

                      const clamped = Math.min(Math.max(raw, 0), maxAllowed);
                      setWalletCreditValue(String(clamped));
                    }}
                    className="w-44 rounded border p-2"
                    placeholder="Valor do crédito"
                    />

                    )}

                    </div>

                    )}

                    </div>
                    )}

                    {loadingPatientTreatments ? (
                      <p className="text-sm text-purple-700">Carregando tratamentos...</p>
                    ) : patientTreatments.length > 0 ? (
                      <div className="space-y-2">
                        {patientTreatments.map((treatment) => (
                          <div
                            key={treatment.id}
                            className="flex items-center justify-between gap-3 bg-white border border-purple-100 rounded-lg p-3"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {treatment.procedure}
                              </p>
                              <p className="text-xs text-gray-500">
                                Status: {treatment.status || '—'} · Valor:{' '}
                                {formatCurrency(treatment.final_cost ?? treatment.cost ?? 0)}
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() => addTreatmentToInvoice(treatment)}
                              className="px-3 py-1.5 rounded-md bg-purple-600 text-white text-xs hover:bg-purple-700"
                            >
                              Adicionar à fatura
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-purple-700">
                        Nenhum tratamento salvo para este paciente.
                      </p>
                    )}
                  </div>
                )}

                {/* Items */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Itens da Fatura *
                    </label>
                    <button
                      type="button"
                      onClick={addInvoiceItem}
                      className="text-purple-600 hover:text-purple-700 text-sm flex items-center"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Adicionar Item
                    </button>
                  </div>

                  <div className="space-y-3">
                    {invoiceData.items.map((item, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-1 md:grid-cols-5 gap-3 p-3 border border-gray-200 rounded-lg"
                      >
                        <div className="md:col-span-2">
                          <input
                            type="text"
                            placeholder="Descrição do serviço"
                            value={item.description}
                            onChange={(e) =>
                              updateInvoiceItem(
                                index,
                                'description',
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                            required
                          />
                        </div>
                        <div>
                          <input
                            type="number"
                            placeholder="Qtd"
                            min="0.01"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) =>
                              updateInvoiceItem(
                                index,
                                'quantity',
                                parseFloat(e.target.value) || 0
                              )
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                            required
                          />
                        </div>
                        <div>
                          <input
                            type="number"
                            placeholder="Valor unitário"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) =>
                              updateInvoiceItem(
                                index,
                                'unit_price',
                                parseFloat(e.target.value) || 0
                              )
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                            required
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">
                            {formatCurrency(
                              item.quantity * item.unit_price
                            )}
                          </span>
                          {invoiceData.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeInvoiceItem(index)}
                              className="text-red-600 hover:text-red-700 p-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals and Discounts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Desconto (R$)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={invoiceData.discount_amount}
                          onChange={(e) =>
                            setInvoiceData({
                              ...invoiceData,
                              discount_amount:
                                parseFloat(e.target.value) || 0,
                              discount_percentage: 0
                            })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Desconto (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={invoiceData.discount_percentage}
                          onChange={(e) =>
                            setInvoiceData({
                              ...invoiceData,
                              discount_percentage:
                                parseFloat(e.target.value) || 0,
                              discount_amount: 0
                            })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Taxa/Imposto (R$)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={invoiceData.tax_amount}
                          onChange={(e) =>
                            setInvoiceData({
                              ...invoiceData,
                              tax_amount:
                                parseFloat(e.target.value) || 0,
                              tax_percentage: 0
                            })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Taxa/Imposto (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={invoiceData.tax_percentage}
                          onChange={(e) =>
                            setInvoiceData({
                              ...invoiceData,
                              tax_percentage:
                                parseFloat(e.target.value) || 0,
                              tax_amount: 0
                            })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Resumo
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Subtotal:</span>
                        <span>{formatCurrency(calculateInvoiceSubtotal())}</span>
                      </div>
                      {(invoiceData.discount_amount > 0 ||
                        invoiceData.discount_percentage > 0) && (
                        <div className="flex justify-between text-red-600">
                          <span>Desconto:</span>
                          <span>
                            -
                            {formatCurrency(
                              invoiceData.discount_percentage > 0
                                ? calculateInvoiceSubtotal() *
                                    (invoiceData.discount_percentage /
                                      100)
                                : invoiceData.discount_amount
                            )}
                          </span>
                        </div>
                      )}
                      {(invoiceData.tax_amount > 0 ||
                        invoiceData.tax_percentage > 0) && (
                        <div className="flex justify-between">
                          <span>Taxa/Imposto:</span>
                          <span>
                            {formatCurrency(
                              invoiceData.tax_percentage > 0
                                ? (calculateInvoiceSubtotal() -
                                    (invoiceData.discount_percentage > 0
                                      ? calculateInvoiceSubtotal() *
                                        (invoiceData.discount_percentage /
                                          100)
                                      : invoiceData.discount_amount)) *
                                    (invoiceData.tax_percentage / 100)
                                : invoiceData.tax_amount
                            )}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg border-t pt-2">
                        <span>Total:</span>
                        <span>{formatCurrency(calculateInvoiceTotal())}</span>
                      </div>

                      {getWalletCreditAmount() > 0 && (
                        <>
                          <div className="flex justify-between text-green-700">
                            <span>Crédito utilizado:</span>
                            <span>-{formatCurrency(getWalletCreditAmount())}</span>
                          </div>

                          <div className="flex justify-between font-bold text-lg border-t pt-2">
                            <span>Saldo a receber:</span>
                            <span>{formatCurrency(getInvoiceRemainingAmount())}</span>
                          </div>

                          {getInvoiceRemainingAmount() === 0 && (
                            <div className="rounded-lg bg-green-100 text-green-800 text-xs font-medium px-3 py-2 mt-2">
                              Esta fatura será criada como paga com o crédito do paciente.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {!editingInvoice && (
                  <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
                    <p className="text-sm font-semibold text-purple-900">Forma de recebimento</p>
                    <div className="mt-3 flex flex-wrap gap-5">
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          checked={invoiceData.payment_type === 'single'}
                          onChange={() => setInvoiceData({
                            ...invoiceData,
                            payment_type: 'single',
                            installment_count: 1,
                            first_installment_date: ''
                          })}
                        />
                        À vista
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          checked={invoiceData.payment_type === 'installment'}
                          onChange={() => setInvoiceData({
                            ...invoiceData,
                            payment_type: 'installment',
                            installment_count: Math.max(Number(invoiceData.installment_count || 2), 2),
                            first_installment_date: invoiceData.first_installment_date || invoiceData.due_date
                          })}
                        />
                        Parcelado
                      </label>
                    </div>

                    {invoiceData.payment_type === 'installment' && (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantidade de parcelas *
                          </label>
                          <input
                            type="number"
                            min="2"
                            max="120"
                            value={invoiceData.installment_count}
                            onChange={(e) => setInvoiceData({
                              ...invoiceData,
                              installment_count: e.target.value
                            })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Primeiro vencimento *
                          </label>
                          <input
                            type="date"
                            required
                            value={invoiceData.first_installment_date}
                            onChange={(e) => setInvoiceData({
                              ...invoiceData,
                              first_installment_date: e.target.value,
                              due_date: e.target.value
                            })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Condições de Pagamento
                    </label>
                    <input
                      type="text"
                      value={invoiceData.payment_terms}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          payment_terms: e.target.value
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                      placeholder="Ex: À vista, 30 dias, etc."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={invoiceData.notes}
                    onChange={(e) =>
                      setInvoiceData({
                        ...invoiceData,
                        notes: e.target.value
                      })
                    }
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="Observações adicionais..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={resetInvoiceForm}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {editingInvoice ? 'Atualizar' : 'Criar'} Fatura
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Payment Form Modal */}
        {showPaymentForm && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                resetPaymentForm();
              }
            }}
          >
            <div
              className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Registrar Pagamento
                  {selectedInvoice && (
                    <span className="text-sm font-normal text-gray-600 block">
                      Fatura #{selectedInvoice.invoice_number} -{' '}
                      {selectedInvoice.patient_name}
                      {selectedInvoiceInstallment && (
                        <> · Parcela {selectedInvoiceInstallment.installment_number}/{selectedInvoiceInstallment.total_installments}</>
                      )}
                    </span>
                  )}
                </h3>
              </div>

              <form onSubmit={handlePaymentSubmit} className="p-6 space-y-4">
                {!selectedInvoice && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Destino do pagamento
                    </label>

                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="radio"
                          checked={paymentDestination === 'invoice'}
                          onChange={() => {
                            setPaymentDestination('invoice');
                            setPaymentData((prev) => ({
                              ...prev,
                              patient_id: '',
                              patient_name: '',
                            }));
                          }}
                        />
                        Quitar uma fatura existente
                      </label>

                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="radio"
                          checked={paymentDestination === 'wallet'}
                          onChange={() => {
                            setPaymentDestination('wallet');
                            setPaymentData((prev) => ({
                              ...prev,
                              invoice_id: '',
                              amount: prev.amount || 0,
                            }));
                          }}
                        />
                        Adicionar como crédito do paciente
                      </label>
                    </div>
                  </div>
                )}
                {!selectedInvoice && paymentDestination === 'invoice' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fatura *
                    </label>
                    <select
                      required={paymentDestination === 'invoice'}
                      value={paymentData.invoice_id}
                      onChange={(e) => {
                        const invoice = invoices.find(
                          (inv) => inv.id === e.target.value
                        );

                        setPaymentData({
                          ...paymentData,
                          invoice_id: e.target.value,
                          invoice_installment_id: '',
                          patient_id: invoice?.patient_id || '',
                          patient_name: invoice?.patient_name || '',
                          amount: invoice
                            ? Math.max(Number(invoice.total_amount || 0) - Number(invoice.received_amount || 0), 0)
                            : 0
                        });
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Selecione uma fatura</option>
                      {invoices
                        .filter(
                          (inv) =>
                            inv.status !== 'pago' &&
                            inv.status !== 'cancelado' &&
                            inv.payment_type !== 'installment'
                        )
                        .map((invoice) => (
                          <option key={invoice.id} value={invoice.id}>
                            #{invoice.invoice_number} - {invoice.patient_name} -{' '}
                            {formatCurrency(invoice.total_amount)}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                {!selectedInvoice && paymentDestination === 'wallet' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Paciente *
                    </label>
                    <select
                      required={paymentDestination === 'wallet'}
                      value={paymentData.patient_id}
                      onChange={(e) => {
                        const patient = patients.find((p) => p.id === e.target.value);

                        setPaymentData({
                          ...paymentData,
                          patient_id: e.target.value,
                          patient_name: patient?.full_name || patient?.name || '',
                          invoice_id: '',
                        });
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Selecione um paciente</option>
                      {patients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.full_name || patient.name}
                        </option>
                      ))}
                    </select>

                    <p className="text-xs text-gray-500 mt-1">
                      O valor será registrado como crédito disponível na carteira do paciente.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {paymentDestination === 'wallet' ? 'Valor do crédito *' : 'Valor do Pagamento *'}
                    </label>
                    <input
                      type="number"
                      required
                      min="0.01"
                      step="0.01"
                      value={paymentData.amount}
                      onChange={(e) =>
                        setPaymentData({
                          ...paymentData,
                          amount: parseFloat(e.target.value) || 0
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Método de Pagamento *
                    </label>
                    <select
                      required
                      value={paymentData.payment_method_id}
                      onChange={(e) => {
                        const method = paymentMethods.find(
                          (m) => m.id === e.target.value
                        );
                        setPaymentData({
                          ...paymentData,
                          payment_method_id: e.target.value,
                          payment_method_name: method ? method.name : ''
                        });
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Selecione um método</option>
                      {paymentMethods.map((method) => (
                        <option key={method.id} value={method.id}>
                          {method.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID da Transação
                    </label>
                    <input
                      type="text"
                      value={paymentData.transaction_id}
                      onChange={(e) =>
                        setPaymentData({
                          ...paymentData,
                          transaction_id: e.target.value
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                      placeholder="ID PIX, comprovante, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Referência
                    </label>
                    <input
                      type="text"
                      value={paymentData.reference}
                      onChange={(e) =>
                        setPaymentData({
                          ...paymentData,
                          reference: e.target.value
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                      placeholder="Referência adicional"
                    />
                  </div>
                </div>

                {paymentMethods.find(
                  (m) => m.id === paymentData.payment_method_id
                )?.installments_allowed && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Parcela Número
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={paymentData.installment_number || ''}
                        onChange={(e) =>
                          setPaymentData({
                            ...paymentData,
                            installment_number:
                              parseInt(e.target.value) || null
                          })
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total de Parcelas
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={paymentData.total_installments || ''}
                        onChange={(e) =>
                          setPaymentData({
                            ...paymentData,
                            total_installments:
                              parseInt(e.target.value) || null
                          })
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        notes: e.target.value
                      })
                    }
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="Observações sobre o pagamento..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={resetPaymentForm}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    {paymentDestination === 'wallet' ? 'Registrar Crédito' : 'Registrar Pagamento'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de detalhes da NFS-e (novo) */}
        {nfseDetailsModalOpen && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: 8,
                padding: 20,
                maxWidth: 800,
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
              }}
            >
              <h2
                style={{
                  marginBottom: 10,
                  fontSize: 18,
                  fontWeight: 600
                }}
              >
                Detalhes da NFS-e
              </h2>

              {nfseDetailsLoading && <p>Carregando detalhes da NFS-e...</p>}

              {!nfseDetailsLoading && nfseDetailsData && (
                <>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 8,
                      marginBottom: 12
                    }}
                  >
                    <p style={{ margin: 0, fontSize: 14 }}>
                      <strong>Fatura:</strong>{' '}
                      {nfseDetailsData.invoice_number || '-'}
                    </p>
                    <p style={{ margin: 0, fontSize: 14 }}>
                      <strong>Paciente:</strong>{' '}
                      {nfseDetailsData.patient_name || '-'}
                    </p>
                    <p style={{ margin: 0, fontSize: 14 }}>
                      <strong>Valor:</strong>{' '}
                      {nfseDetailsData.total_amount != null
                        ? nfseDetailsData.total_amount.toLocaleString(
                            'pt-BR',
                            {
                              style: 'currency',
                              currency: 'BRL'
                            }
                          )
                        : '-'}
                    </p>
                    <p style={{ margin: 0, fontSize: 14 }}>
                      <strong>Status da NFS-e:</strong>{' '}
                      {nfseDetailsData.nfse_status || 'nao_emitida'}
                    </p>
                    <p style={{ margin: 0, fontSize: 14 }}>
                      <strong>Ambiente:</strong>{' '}
                      {nfseDetailsData.nfse_ambiente || '—'}
                    </p>
                    <p style={{ margin: 0, fontSize: 14 }}>
                      <strong>Número:</strong>{' '}
                      {nfseDetailsData.nfse_numero || '—'}
                    </p>
                    <p style={{ margin: 0, fontSize: 14 }}>
                      <strong>Série:</strong>{' '}
                      {nfseDetailsData.nfse_serie || '—'}
                    </p>
                    <p style={{ margin: 0, fontSize: 14 }}>
                      <strong>Chave de acesso:</strong>{' '}
                      {nfseDetailsData.nfse_chave_acesso || '—'}
                    </p>
                    <p style={{ margin: 0, fontSize: 14 }}>
                      <strong>Cód. verificação:</strong>{' '}
                      {nfseDetailsData.nfse_codigo_verificacao || '—'}
                    </p>
                    <p style={{ margin: 0, fontSize: 14 }}>
                      <strong>Data emissão:</strong>{' '}
                      {nfseDetailsData.nfse_data_emissao
                        ? new Date(
                            nfseDetailsData.nfse_data_emissao
                          ).toLocaleString('pt-BR')
                        : '—'}
                    </p>
                  </div>

                  {nfseDetailsData.nfse_mensagem_erro && (
                    <p
                      style={{
                        marginTop: 0,
                        marginBottom: 12,
                        fontSize: 13,
                        color: '#b91c1c'
                      }}
                    >
                      <strong>Mensagem:</strong>{' '}
                      {nfseDetailsData.nfse_mensagem_erro}
                    </p>
                  )}

                  <div style={{ marginBottom: 12 }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: 14,
                        fontWeight: 500,
                        marginBottom: 4
                      }}
                    >
                      XML da NFS-e (simulado)
                    </label>
                    <textarea
                      readOnly
                      value={nfseDetailsData.nfse_xml || 'Nenhum XML disponível.'}
                      rows={10}
                      style={{
                        width: '100%',
                        borderRadius: 6,
                        border: '1px solid #ccc',
                        padding: 8,
                        fontSize: 12,
                        fontFamily: 'monospace',
                        whiteSpace: 'pre'
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      marginTop: 8
                    }}
                  >
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        onClick={handleDownloadNfseXml}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 6,
                          border: '1px solid #4b5563',
                          backgroundColor: '#fff',
                          cursor: 'pointer',
                          fontSize: 14
                        }}
                      >
                        Baixar XML
                      </button>

                      <button
                        type="button"
                        onClick={handleDownloadNfsePdf}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 6,
                          border: '1px solid #4b5563',
                          backgroundColor: '#fff',
                          cursor: 'pointer',
                          fontSize: 14
                        }}
                      >
                        Baixar PDF
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={handleCloseNfseDetails}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: 'none',
                        backgroundColor: '#4f46e5',
                        color: '#fff',
                        fontWeight: 500,
                        cursor: 'pointer',
                        fontSize: 14
                      }}
                    >
                      Fechar
                    </button>
                  </div>
                </>
              )}

              {!nfseDetailsLoading && !nfseDetailsData && (
                <div style={{ marginTop: 16 }}>
                  <p>Não foi possível carregar os dados da NFS-e.</p>
                  <div style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={handleCloseNfseDetails}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: 'none',
                        backgroundColor: '#4f46e5',
                        color: '#fff',
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      Fechar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal de emissão de NFS-e */}
        {nfseModalOpen && nfseModalInvoice && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '20px',
                maxWidth: '600px',
                width: '100%',
                boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
              }}
            >
              <h2
                style={{
                  marginBottom: '10px',
                  fontSize: '18px',
                  fontWeight: 600
                }}
              >
                Emitir NFS-e
              </h2>

              <p style={{ margin: '4px 0', fontSize: '14px' }}>
                <strong>Fatura:</strong> {nfseModalInvoice.invoice_number}
              </p>
              <p style={{ margin: '4px 0', fontSize: '14px' }}>
                <strong>Paciente:</strong> {nfseModalInvoice.patient_name}
              </p>
              <p
                style={{
                  margin: '4px 0 12px',
                  fontSize: '14px'
                }}
              >
                <strong>Valor:</strong>{' '}
                {nfseModalInvoice.total_amount != null
                  ? nfseModalInvoice.total_amount.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    })
                  : '—'}
              </p>

              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '4px'
                }}
              >
                Descrição / Observações da NFS-e
              </label>
              <textarea
                value={nfseModalDescription}
                onChange={(e) => setNfseModalDescription(e.target.value)}
                rows={6}
                style={{
                  width: '100%',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  padding: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  marginBottom: '16px',
                  whiteSpace: 'pre-wrap'
                }}
                placeholder="Descreva aqui os serviços prestados. Ex.:&#10;1. Profilaxia dentária - Qtd: 1 - Total: R$ 150,00&#10;2. Restauração em resina composta - Qtd: 1 - Total: R$ 150,00"
              />

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                  marginTop: '8px'
                }}
              >
                <button
                  type="button"
                  onClick={handleCloseNfseModal}
                  disabled={nfseModalSaving}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #aaa',
                    backgroundColor: '#f5f5f5',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmNfseEmission}
                  disabled={nfseModalSaving}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: '#4f46e5',
                    color: '#fff',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  {nfseModalSaving ? 'Emitindo...' : 'Confirmar emissão'}
                </button>
              </div>
            </div>
          </div>
        )}
        {showImportedNfseViewer && selectedImportedNfse && (
          <ImportedNfseViewer
            nfse={selectedImportedNfse}
            invoice={invoices.find((invoice) => invoice.id === selectedImportedNfse.invoice_id)}
            onOpenLink={(nfse) => {
              setShowImportedNfseViewer(false);
              openImportedNfseLinkModal(nfse);
            }}
            onClose={() => {
              setShowImportedNfseViewer(false);
              setSelectedImportedNfse(null);
            }}
          />
        )}

        {/* Modal Vincular Fatura */}
        {showLinkInvoiceModal && selectedNfseForLink && (
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-900/60 p-4">
            <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div className="border-b border-gray-200 p-5 sm:p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedNfseForLink.invoice_id ? 'Trocar vínculo da NFS-e' : 'Vincular NFS-e à fatura'}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Nota {selectedNfseForLink.numero_nfse || 'sem número'} · {selectedNfseForLink.tomador_nome || 'Tomador não identificado'}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-purple-100 px-3 py-1 text-sm font-semibold text-purple-800">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(selectedNfseForLink.valor_servico || 0))}
                  </span>
                </div>
              </div>

              <div className="max-h-[60vh] space-y-3 overflow-y-auto p-5 sm:p-6">
                {getNfseCandidateInvoices(selectedNfseForLink).length === 0 ? (
                  <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-6 text-center">
                    <p className="font-medium text-amber-900">Nenhuma fatura compatível foi localizada.</p>
                    <p className="mt-1 text-sm text-amber-800">
                      Confira se o tomador foi associado a um paciente e se há uma fatura cadastrada para ele.
                    </p>
                  </div>
                ) : (
                  getNfseCandidateInvoices(selectedNfseForLink).map(({ invoice, difference, daysDifference, score }, index) => {
                    const valuesMatch = difference < 0.01;
                    const selected = linkInvoiceId === invoice.id;
                    return (
                      <button
                        type="button"
                        key={invoice.id}
                        onClick={() => setLinkInvoiceId(invoice.id)}
                        className={`w-full rounded-xl border p-4 text-left transition ${selected ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-100' : 'border-gray-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/40'}`}
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-gray-900">Fatura #{invoice.invoice_number}</span>
                              {index === 0 && score >= 80 && (
                                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">Melhor correspondência</span>
                              )}
                              {selectedNfseForLink.invoice_id === invoice.id && (
                                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">Vínculo atual</span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-gray-600">{invoice.patient_name || selectedNfseForLink.tomador_nome || 'Paciente não identificado'}</p>
                            <p className="mt-1 text-xs text-gray-500">
                              Emitida em {invoice.issue_date ? new Date(invoice.issue_date).toLocaleDateString('pt-BR') : 'data não informada'}
                              {daysDifference !== null ? ` · ${daysDifference} dia(s) de diferença` : ''}
                              {invoice.status ? ` · ${String(invoice.status).replaceAll('_', ' ')}` : ''}
                            </p>
                          </div>
                          <div className="text-left sm:text-right">
                            <p className="font-semibold text-gray-900">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(invoice.total_amount || 0))}
                            </p>
                            <p className={`mt-1 text-xs font-medium ${valuesMatch ? 'text-green-700' : 'text-amber-700'}`}>
                              {valuesMatch
                                ? 'Valor compatível com a nota'
                                : `Diferença de ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(difference)}`}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-gray-200 bg-gray-50 p-5 sm:flex-row sm:justify-end sm:p-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowLinkInvoiceModal(false);
                    setSelectedNfseForLink(null);
                    setLinkInvoiceId('');
                  }}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleLinkInvoice}
                  disabled={isLinking || !linkInvoiceId}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLinking ? 'Salvando vínculo...' : selectedNfseForLink.invoice_id ? 'Confirmar troca' : 'Confirmar vínculo'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BillingPage;
